import { Accordion } from '@monorepo/shared/componentsV2/Accordion';
import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useFormTemplate } from './hooks/useFormTemplate';

const Summary = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 1rem 0.5rem;
`;

const StyledLabel = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
`;

const StyledAccordion = styled(Accordion)`
  &::before {
    background-color: transparent;
  }

  .MuiAccordionSummary-root:not(:focus-visible) {
    background-color: ${({ theme }) => theme.colors.white};
  }

  .MuiAccordionDetails-root {
    padding: 0;
  }

  .accordion-details-container {
    max-height: 20rem;
    overflow: auto;
  }
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SiteContainer = styled.div`
  padding: 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.grayeee};
`;

const SiteName = styled.div`
  margin: 0.3rem 1rem;
  color: ${({ theme }) => theme.colors.gray999};
`;

const CalendarContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CalendarItem = styled.div`
  margin: 0.3rem 1rem;
`;

type FormTemplateUsageProps = {
  formTemplateSlug: string;
  organizationId: string;
};

function NoUsageIndicator() {
  return (
    <SiteContainer>
      <SiteName>This form is not used by any sites.</SiteName>
    </SiteContainer>
  );
}

// todo@formTemplates: remove this component once the isAssigningFormCalendarsEnabled feature flag is removed (also remove matching BE),
// unless it continues to be shown for site admins
export function FormTemplateUsage({
  formTemplateSlug,
  organizationId,
}: FormTemplateUsageProps) {
  const { formTemplate } = useFormTemplate({
    organizationId,
    slug: formTemplateSlug,
  });
  const formTemplateUsage = formTemplate?.usage;
  const usageCount = formTemplateUsage?.length;

  const projectRows = useMemo(() => {
    if (!usageCount) {
      return <NoUsageIndicator />;
    }

    return formTemplateUsage.map((usage) => {
      const calendarRows = usage.formCalendars.map((fc) => (
        <CalendarItem key={fc.id}>{fc.name}</CalendarItem>
      ));
      return (
        <SiteContainer key={usage.projectId}>
          <SiteName>{usage.projectName.toUpperCase()}</SiteName>
          <CalendarContainer>{calendarRows}</CalendarContainer>
        </SiteContainer>
      );
    });
  }, [formTemplateUsage, usageCount]);

  const detailsContent = <DetailsContainer>{projectRows}</DetailsContainer>;

  const summaryContent = (
    <Summary>
      <StyledLabel>
        <span>Sites using this form</span>
        <Tooltip
          aria-label="These are the places this form is used across your sites. To edit this list, please reach out to support@mapistry.com for assistance."
          title={
            <span>
              These are the places this form is used across your sites. To edit
              this list, please reach out to{' '}
              <strong>support@mapistry.com</strong> for assistance.
            </span>
          }
        />
      </StyledLabel>
      <Chip color="secondary" label={usageCount} variant="round" />
    </Summary>
  );

  return (
    <StyledAccordion
      detailsClassName="accordion-details-container"
      color="secondary"
      detailsContent={detailsContent}
      summaryContent={summaryContent}
      variant="contained"
    />
  );
}
